import { useLayoutEffect, useState } from "react"
import { Jute } from "../..";

/**
 * This async hook initially always returns false until the checks for a gyroscope has run.
 * The presence of a gyroscope is checked by the presence of gyroscope specific values (rotation rate) on device motion events 
 * to avoid false positive values. 
 * @returns {boolean} hasGyroscope - The presence of a gyroscope on the device
 */
interface DeviceMotionEventiOS extends DeviceMotionEvent {
    requestPermission?: () => Promise<'granted' | 'denied'>;
}

interface DeviceOrientationEventiOS extends DeviceOrientationEvent {
    requestPermission?: () => Promise<'granted' | 'denied'>;
}

export const useDeviceHasGyroscope = (enable: boolean, isIFrame: boolean): boolean => {
    const [hasGyroscope, setHasGyroscope] = useState(false);

    useLayoutEffect(() => {
        if (!enable || isIFrame) return;
        const requestPermission = (DeviceMotionEvent as unknown as DeviceMotionEventiOS).requestPermission;

        const onDeviceMotion =  (event: DeviceMotionEvent) => {
            if (event.rotationRate?.alpha || event.rotationRate?.beta || event.rotationRate?.gamma) {
                setHasGyroscope(true);
            }
        };

        if (typeof requestPermission === 'function') {

            const requestMotionPermission = () => {
                requestPermission()
                    .then(permissionState => {
                        if (permissionState === 'granted') {
                            window.addEventListener('devicemotion', onDeviceMotion, {once: true});
                        }
                    })
                    .catch(console.error);
            }

            if (Jute.isLaunched) {
                requestMotionPermission();
            } else Jute.onLaunch(requestMotionPermission)

        } else {
            window.addEventListener('devicemotion', onDeviceMotion, {once: true});
        }

        return () => {
            window.removeEventListener('devicemotion', onDeviceMotion)
        }
    }, [enable])

    return hasGyroscope;
}

type IEventOrientation = {alpha: number | null, beta: number | null, gamma: number | null};

export const useGetInitialDeviceOrientation = (enable: boolean): IEventOrientation => {
    const [orientation, setOrientation] = useState<IEventOrientation>({alpha: 0, beta: 0, gamma: 0});

    useLayoutEffect(() => {
        if (!enable) return;
        const requestPermission = (DeviceOrientationEvent as unknown as DeviceOrientationEventiOS).requestPermission;

        const onDeviceOrientation =  (event: DeviceOrientationEvent) => {
            setOrientation({alpha: event.alpha, beta: event.beta, gamma: event.gamma});
        };

        if (typeof requestPermission === 'function') {

            const requestMotionPermission = () => {
                requestPermission()
                    .then(permissionState => {
                        if (permissionState === 'granted') {
                            window.addEventListener('deviceorientation', onDeviceOrientation, {once: true});
                        }
                    })
                    .catch(console.error);
            }

            if (Jute.isLaunched) requestMotionPermission()
            else Jute.onLaunch(requestMotionPermission)
        } else {
            window.addEventListener('deviceorientation', onDeviceOrientation, {once: true});
        }

        return () => {
            window.removeEventListener('deviceorientation', onDeviceOrientation)
        }
    }, [enable])

    return orientation;
}